import { DRAWER_PLACEMENTS } from "../../../../../config/constants";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Divider, Drawer, Empty, Input, InputRef, Tree } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import {
  FilterFilled,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { FilterConfirmProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { DataNode, TreeProps } from "antd/lib/tree";
import MigrationRuleService from "../../../../../services/migrationrules/MigrationRuleService";
import TablesService from "../../../../../services/tables/TablesService";
import { GetTablesRequest } from "../../../../../models/tables/GetTablesRequest";
import Datatable from "../../../../shared/components/Datatable";
import { useTranslation } from "react-i18next";

const { Search } = Input;

const RuleResults = ({
  selectedEntity,
  selectedGroupCode,
  selectable,
  multiSelect,
  selectedRows,
}: {
  selectedEntity: any;
  selectedGroupCode: any;
  selectable: boolean;
  multiSelect: boolean;
  selectedRows: any;
  setSelectedRows: any;
}) => {
  interface DataType {
    key: string;
    groupCode: string;
    fromTableId: number;
    targetTableId: number;
    code: string;
    migrationStatus: string;
  }

  interface DetailsDataType {
    key: string;
    code: string;
    ruleId: number;
    ruleName: string;
    join: string;
    valueColumn: string;
    resultValue: string;
    conditionValue: string;
    mappingMigrationStatus: boolean;
    mappingWeight: number;
    resultMigrationStatus: boolean;
    resultWeight: number;
  }

  const { t, i18n } = useTranslation("translations");

  type DataIndex = keyof DataType;

  const [data, setData] = useState<DataType[]>([]);
  const [detailsData, setDetailsData] = useState<DataType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const searchInput = useRef<InputRef>(null);

  const [groupCodeSearchValue, setGroupCodeSearchValue] = useState<string>("");
  const [groupCodeFilters, setGroupCodeFilters] = useState<any>([]);
  const [groupCodeCheckedKeys, setGroupCodeCheckedKeys] = useState<any>([]);
  const [groupCodeAscending, setGroupCodeAscending] = useState(false);
  const [groupCodeDescending, setGroupCodeDescending] = useState(false);

  const [sourceTableSearchValue, setSourceTableSearchValue] =
    useState<string>("");
  const [sourceTableFilters, setSourceTableFilters] = useState<any>([]);
  const [sourceTableAscending, setSourceTableAscending] = useState(false);
  const [sourceTableDescending, setSourceTableDescending] = useState(false);

  const [targetTableSearchValue, setTargetTableSearchValue] =
    useState<string>("");
  const [targetTableFilters, setTargetTableFilters] = useState<any>([]);
  const [targetTableAscending, setTargetTableAscending] = useState(false);
  const [targetTableDescending, setTargetTableDescending] = useState(false);

  const [codeSearchValue, setCodeSearchValue] = useState<string>("");
  const [codeFilters, setCodeFilters] = useState<any>([]);
  const [codeAscending, setCodeAscending] = useState(false);
  const [codeDescending, setCodeDescending] = useState(false);

  const [migrationStatusSearchValue, setMigrationStatusSearchValue] =
    useState<string>("");
  const [migrationStatusFilters, setMigrationStatusFilters] = useState<any>([]);
  const [migrationStatusAscending, setMigrationStatusAscending] =
    useState(false);
  const [migrationStatusDescending, setMigrationStatusDescending] =
    useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(undefined);

  const scroll = {
    x: 2000,
    y: "calc(100vh - 260px)"
  };

  const scrollMain = { y: "calc(100vh - 330px)", x: "max-content" };

  const onDoubleClickRow = (record: any) => {
    setOpenDrawer(true);
    setSelectedRow(record);
  };

  const onClose = () => {
    setOpenDrawer(false);
  };

  useEffect(() => {
    if (selectedEntity && selectedGroupCode) {
      setIsTableLoading(true);
      const model: GetTablesRequest = {
        entityId: selectedEntity,
        operationId: 2,
        groupCode: "",
      };
      TablesService.getTables(model).then((response: any) => {
        const table = response.data[0];
        if (table) {
          MigrationRuleService.getAllResults(selectedGroupCode, table.id).then(
              (response: any) => {
                setData(response.data);
                setIsTableLoading(false);
              }
          );
        } else {
          setIsTableLoading(false);
        }
      });
    }
  }, [selectedEntity, selectedGroupCode]);

  useEffect(() => {
    if (selectedRow) {
      MigrationRuleService.getAllDetails(
        selectedRow.code,
        selectedRow.groupCode,
        selectedRow.targetTableId
      ).then((response: any) => {
        const responseData = response.data;
        setDetailsData(responseData);
        setIsTableLoading(false);
      });
    }
  }, [selectedRow]);

  useEffect(() => {
    if (data) {
      const distinctBy = (prop: any, arr: any) => [
        ...new Set(arr.map((o: any) => o[prop])),
      ];

      let result = distinctBy("groupCode", data);
      setGroupCodeFilters(
        result.map((value: any, index: any) => ({ key: index, title: value }))
      );

      result = distinctBy("sourceTable", data);
      setSourceTableFilters(
        result.map((value: any, index: any) => ({ key: index, title: value }))
      );

      result = distinctBy("targetTable", data);
      setTargetTableFilters(
        result.map((value: any, index: any) => ({ key: index, title: value }))
      );

      result = distinctBy("code", data);
      setCodeFilters(
        result.map((value: any, index: any) => ({ key: index, title: value }))
      );

      result = distinctBy("migrationStatus", data);
      setMigrationStatusFilters(
        result.map((value: any, index: any) => ({ key: index, title: value }))
      );
    }
  }, [data]);

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const onGroupCodeFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setGroupCodeSearchValue(value);
  };

  const onSourceTableFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setSourceTableSearchValue(value);
  };

  const onTargetTableFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setTargetTableSearchValue(value);
  };

  const onCodeFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCodeSearchValue(value);
  };

  const onMigrationStatusFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setMigrationStatusSearchValue(value);
  };

  const groupCodeFilterTreeData = useMemo(() => {
    const loop = (groupCodeFilters: DataNode[]): DataNode[] =>
      groupCodeFilters
        .map((item: any) => {
          const strTitle = item.title as string;
          const index = strTitle.indexOf(groupCodeSearchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(index + groupCodeSearchValue.length);
          const title =
            index > -1 ? (
              <span>
                {beforeStr}
                <span className="site-tree-search-value">
                  {groupCodeSearchValue}
                </span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            );

          return {
            title,
            key: item.key,
            shouldRemove: index === -1,
          };
        })
        .filter((x: any) => !x.shouldRemove);

    return loop(
      groupCodeFilters.map((x: any) => ({ key: x.key, title: x.title }))
    );
  }, [groupCodeFilters, groupCodeSearchValue]);

  const sourceTableFilterTreeData = useMemo(() => {
    const loop = (sourceTableFilters: DataNode[]): DataNode[] =>
      sourceTableFilters
        .map((item: any) => {
          const strTitle = item.title ? item.title.toString() : "";
          const index = strTitle.indexOf(sourceTableSearchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(
            index + sourceTableSearchValue.length
          );
          const title =
            index > -1 ? (
              <span>
                {beforeStr}
                <span className="site-tree-search-value">
                  {sourceTableSearchValue}
                </span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            );

          return {
            title,
            key: item.key,
            shouldRemove: index === -1,
          };
        })
        .filter((x: any) => !x.shouldRemove);

    return loop(
      sourceTableFilters.map((x: any) => ({ key: x.key, title: x.title }))
    );
  }, [sourceTableFilters, sourceTableSearchValue]);

  const targetTableFilterTreeData = useMemo(() => {
    const loop = (targetTableFilters: DataNode[]): DataNode[] =>
      targetTableFilters
        .map((item: any) => {
          const strTitle = item.title ? item.title.toString() : "";
          const index = strTitle.indexOf(targetTableSearchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(
            index + targetTableSearchValue.length
          );
          const title =
            index > -1 ? (
              <span>
                {beforeStr}
                <span className="site-tree-search-value">
                  {targetTableSearchValue}
                </span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            );

          return {
            title,
            key: item.key,
            shouldRemove: index === -1,
          };
        })
        .filter((x: any) => !x.shouldRemove);

    return loop(
      targetTableFilters.map((x: any) => ({ key: x.key, title: x.title }))
    );
  }, [targetTableFilters, targetTableSearchValue]);

  const codeFilterTreeData = useMemo(() => {
    const loop = (codeFilters: DataNode[]): DataNode[] =>
      codeFilters
        .map((item: any) => {
          const strTitle = item.title ? (item.title as string) : "";
          const index = strTitle.indexOf(codeSearchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(index + codeSearchValue.length);
          const title =
            index > -1 ? (
              <span>
                {beforeStr}
                <span className="site-tree-search-value">
                  {codeSearchValue}
                </span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            );

          return {
            title,
            key: item.key,
            shouldRemove: index === -1,
          };
        })
        .filter((x: any) => !x.shouldRemove);

    return loop(codeFilters.map((x: any) => ({ key: x.key, title: x.title })));
  }, [codeFilters, codeSearchValue]);

  const migrationStatusFilterTreeData = useMemo(() => {
    const loop = (migrationStatusFilters: DataNode[]): DataNode[] =>
      migrationStatusFilters
        .map((item: any) => {
          const strTitle = item.title ? item.title.toString() : "";
          const index = strTitle.indexOf(migrationStatusSearchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(
            index + migrationStatusSearchValue.length
          );
          const title =
            index > -1 ? (
              <span>
                {beforeStr}
                <span className="site-tree-search-value">
                  {migrationStatusSearchValue}
                </span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            );

          return {
            title,
            key: item.key,
            shouldRemove: index === -1,
          };
        })
        .filter((x: any) => !x.shouldRemove);

    return loop(
      migrationStatusFilters.map((x: any) => ({ key: x.key, title: x.title }))
    );
  }, [migrationStatusFilters, migrationStatusSearchValue]);

  useEffect(() => {
    if (data) {
      let newTableItems = data.map((x: any) => {
        return { ...x };
      });
      if (groupCodeAscending) {
        setData([
          ...newTableItems.sort((a, b) => (a.groupCode > b.groupCode ? 1 : -1)),
        ]);
      }
      if (groupCodeDescending) {
        setData([
          ...newTableItems.sort((a, b) => (a.groupCode < b.groupCode ? 1 : -1)),
        ]);
      }
    }
  }, [groupCodeAscending, groupCodeDescending]);

  useEffect(() => {
    if (data) {
      let newTableItems = data.map((x: any) => {
        return { ...x };
      });
      if (sourceTableAscending) {
        setData([
          ...newTableItems.sort((a, b) =>
            a.fromTableId > b.fromTableId ? 1 : -1
          ),
        ]);
      }
      if (sourceTableDescending) {
        setData([
          ...newTableItems.sort((a, b) =>
            a.fromTableId < b.fromTableId ? 1 : -1
          ),
        ]);
      }
    }
  }, [sourceTableAscending, sourceTableDescending]);

  useEffect(() => {
    if (data) {
      let newTableItems = data.map((x: any) => {
        return { ...x };
      });
      if (targetTableAscending) {
        setData([
          ...newTableItems.sort((a, b) =>
            a.targetTableId > b.targetTableId ? 1 : -1
          ),
        ]);
      }
      if (targetTableDescending) {
        setData([
          ...newTableItems.sort((a, b) =>
            a.targetTableId < b.targetTableId ? 1 : -1
          ),
        ]);
      }
    }
  }, [targetTableAscending, targetTableDescending]);

  useEffect(() => {
    if (data) {
      let newTableItems = data.map((x: any) => {
        return { ...x };
      });
      if (codeAscending) {
        setData([...newTableItems.sort((a, b) => (a.code > b.code ? 1 : -1))]);
      }
      if (codeDescending) {
        setData([...newTableItems.sort((a, b) => (a.code < b.code ? 1 : -1))]);
      }
    }
  }, [codeAscending, codeDescending]);

  useEffect(() => {
    if (data) {
      let newTableItems = data.map((x: any) => {
        return { ...x };
      });
      if (migrationStatusAscending) {
        setData([
          ...newTableItems.sort((a, b) =>
            a.migrationStatus > b.migrationStatus ? 1 : -1
          ),
        ]);
      }
      if (migrationStatusDescending) {
        setData([
          ...newTableItems.sort((a, b) =>
            a.migrationStatus < b.migrationStatus ? 1 : -1
          ),
        ]);
      }
    }
  }, [migrationStatusAscending, migrationStatusDescending]);

  const onGroupCodeTreeItemCheck: TreeProps["onCheck"] = (keys, info) => {
    setGroupCodeCheckedKeys(keys);
  };
  const onSourceTableTreeItemCheck: TreeProps["onCheck"] = (keys, info) => {
    setGroupCodeCheckedKeys(keys);
  };
  const onTargetTableTreeItemCheck: TreeProps["onCheck"] = (keys, info) => {
    setGroupCodeCheckedKeys(keys);
  };
  const onCodeTreeItemCheck: TreeProps["onCheck"] = (keys, info) => {
    setGroupCodeCheckedKeys(keys);
  };
  const onMigrationStatusTreeItemCheck: TreeProps["onCheck"] = (keys, info) => {
    setGroupCodeCheckedKeys(keys);
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex,
    filterChangeFunc: any,
    data: any,
    checkedKeys: any,
    onTreeItemCheck: any,
    itemsAcending: any,
    itemsDescending: any,
    setItemsAscending: any,
    setItemsDescending: any
  ): ColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        className="tableFilter"
        style={{ padding: "4px 10px", maxWidth: "220px" }}
      >
        <div className="sortingContainer">
          <span>
            {t("General.Labels.Sort", {
              lngs: i18n.languages,
              defaultValue: "Sort",
            })}
          </span>
          <Button
            type="text"
            icon={<ArrowUpOutlined />}
            style={{
              textAlign: "left",
              paddingLeft: "8px",
              backgroundColor: itemsAcending ? "#7BBDB1" : "",
              color: itemsAcending ? "#fff" : "",
            }}
            onClick={() => {
              setItemsAscending(!itemsAcending);
              setItemsDescending(false);
            }}
          >
            {t("General.Labels.Ascending", {
              lngs: i18n.languages,
              defaultValue: "Ascending",
            })}
          </Button>
          <Button
            type="text"
            icon={<ArrowDownOutlined />}
            style={{
              textAlign: "left",
              paddingLeft: "8px",
              backgroundColor: itemsDescending ? "#7BBDB1" : "",
              color: itemsDescending ? "#fff" : "",
            }}
            onClick={() => {
              setItemsDescending(!itemsDescending);
              setItemsAscending(false);
            }}
          >
            {t("General.Labels.Descending", {
              lngs: i18n.languages,
              defaultValue: "Descending",
            })}
          </Button>
        </div>
        <Divider />
        <Search
          prefix={<SearchOutlined />}
          allowClear
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            filterChangeFunc(e);
            setSelectedKeys(e.target.value ? [e.target.value] : []);
          }}
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "flex" }}
        />
        <Tree
          checkable
          onCheck={onTreeItemCheck}
          checkedKeys={checkedKeys}
          treeData={data}
        />
        <Divider />
        <div className="filterButtons">
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            type="text"
          >
            {t("General.Labels.Reset", {
              lngs: i18n.languages,
              defaultValue: "Reset",
            })}
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
          >
            {t("General.Labels.Ok", {
              lngs: i18n.languages,
              defaultValue: "OK",
            })}
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<DataType> = [
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.Table.Headers.GroupCode",
        {
          lngs: i18n.languages,
          defaultValue: "Group Code",
        }
      ),
      dataIndex: "groupCode",
      key: "groupCode",
      width: "200px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      ...getColumnSearchProps(
        "groupCode",
        onGroupCodeFilterChange,
        groupCodeFilterTreeData,
        groupCodeCheckedKeys,
        onGroupCodeTreeItemCheck,
        groupCodeAscending,
        groupCodeDescending,
        setGroupCodeAscending,
        setGroupCodeDescending
      ),
      sorter: (a, b) => a.groupCode ? a.groupCode.localeCompare(b.groupCode ?? "") : "".localeCompare(b.groupCode ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.Table.Headers.SourceTable",
        {
          lngs: i18n.languages,
          defaultValue: "Source Table",
        }
      ),
      dataIndex: "fromTableId",
      key: "fromTableId",
      width: "150px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      ...getColumnSearchProps(
        "fromTableId",
        onSourceTableFilterChange,
        sourceTableFilterTreeData,
        groupCodeCheckedKeys,
        onSourceTableTreeItemCheck,
        sourceTableAscending,
        sourceTableDescending,
        setSourceTableAscending,
        setSourceTableDescending
      ),
      sorter: (a, b) => a.fromTableId - b.fromTableId,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.Table.Headers.TargetTable",
        {
          lngs: i18n.languages,
          defaultValue: "Target Table",
        }
      ),
      dataIndex: "targetTableId",
      key: "targetTableId",
      width: "150px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      ...getColumnSearchProps(
        "targetTableId",
        onTargetTableFilterChange,
        targetTableFilterTreeData,
        groupCodeCheckedKeys,
        onTargetTableTreeItemCheck,
        targetTableAscending,
        targetTableDescending,
        setTargetTableAscending,
        setTargetTableDescending
      ),
      sorter: (a, b) => a.targetTableId - b.targetTableId,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.Table.Headers.Code",
        {
          lngs: i18n.languages,
          defaultValue: "Code",
        }
      ),
      key: "code",
      dataIndex: "code",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      ...getColumnSearchProps(
        "code",
        onCodeFilterChange,
        codeFilterTreeData,
        groupCodeCheckedKeys,
        onCodeTreeItemCheck,
        codeAscending,
        codeDescending,
        setCodeAscending,
        setCodeDescending
      ),
      sorter: (a, b) => a.code ? a.code.localeCompare(b.code ?? "") : "".localeCompare(b.code ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.Table.Headers.MigrationStatus",
        {
          lngs: i18n.languages,
          defaultValue: "Migration Status",
        }
      ),
      key: "migrationStatus",
      dataIndex: "migrationStatus",
      filters: [
        {
          text: t(
            "Page.Operation.MigrationRules.Tabs.RuleResults.Label.Migration",
            {
              lngs: i18n.languages,
              defaultValue: "MIGRATION",
            }
          ),
          value: "MIGRATION",
        },
        {
          text: t(
            "Page.Operation.MigrationRules.Tabs.RuleResults.Label.NoMigration",
            {
              lngs: i18n.languages,
              defaultValue: "NO MIGRATION",
            }
          ),
          value: "NO MIGRATION",
        },
      ],
      filterMode: "tree",
      filterSearch: true,
      ...getColumnSearchProps(
        "migrationStatus",
        onMigrationStatusFilterChange,
        migrationStatusFilterTreeData,
        groupCodeCheckedKeys,
        onMigrationStatusTreeItemCheck,
        migrationStatusAscending,
        migrationStatusDescending,
        setMigrationStatusAscending,
        setMigrationStatusDescending
      ),
      sorter: (a, b) => a.migrationStatus ? a.migrationStatus.localeCompare(b.migrationStatus ?? "") : "".localeCompare(b.migrationStatus ?? ""),
    },
  ];

  const columnsDetails: ColumnsType<DetailsDataType> = [
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.Code",
        {
          lngs: i18n.languages,
          defaultValue: "Code",
        }
      ),
      dataIndex: "code",
      key: "code",
      width: "150px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "groupCode",
      //   onGroupCodeFilterChange,
      //   groupCodeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onGroupCodeTreeItemCheck,
      //   groupCodeAscending,
      //   groupCodeDescending,
      //   setGroupCodeAscending,
      //   setGroupCodeDescending
      // ),
      sorter: (a, b) => a.code ? a.code.localeCompare(b.code ?? "") : "".localeCompare(b.code ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.RuleID",
        {
          lngs: i18n.languages,
          defaultValue: "Rule ID",
        }
      ),
      dataIndex: "ruleId",
      key: "ruleId",
      width: "100px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "sourceTableId",
      //   onSourceTableFilterChange,
      //   sourceTableFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onSourceTableTreeItemCheck,
      //   sourceTableAscending,
      //   sourceTableDescending,
      //   setSourceTableAscending,
      //   setSourceTableDescending
      // ),
      sorter: (a, b) => a.ruleId - b.ruleId,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.RuleName",
        {
          lngs: i18n.languages,
          defaultValue: "Rule Name",
        }
      ),
      dataIndex: "ruleName",
      key: "ruleName",
      width: "150px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      render: (value) => value ?? "-",
      // ...getColumnSearchProps(
      //   "targetTableId",
      //   onTargetTableFilterChange,
      //   targetTableFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onTargetTableTreeItemCheck,
      //   targetTableAscending,
      //   targetTableDescending,
      //   setTargetTableAscending,
      //   setTargetTableDescending
      // ),
      sorter: (a, b) => a.ruleName ? a.ruleName.localeCompare(b.ruleName ?? "") : "".localeCompare(b.ruleName ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.Join",
        {
          lngs: i18n.languages,
          defaultValue: "Join",
        }
      ),
      key: "join",
      dataIndex: "join",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      width: "200px",
      // ...getColumnSearchProps(
      //   "code",
      //   onCodeFilterChange,
      //   codeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onCodeTreeItemCheck,
      //   codeAscending,
      //   codeDescending,
      //   setCodeAscending,
      //   setCodeDescending
      // ),
      sorter: (a, b) => a.join ? a.join.localeCompare(b.join ?? "") : "".localeCompare(b.join ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.ValueColumn",
        {
          lngs: i18n.languages,
          defaultValue: "Value Column",
        }
      ),
      key: "valueColumn",
      dataIndex: "valueColumn",
      width: "200px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "code",
      //   onCodeFilterChange,
      //   codeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onCodeTreeItemCheck,
      //   codeAscending,
      //   codeDescending,
      //   setCodeAscending,
      //   setCodeDescending
      // ),
      sorter: (a, b) => a.valueColumn ? a.valueColumn.localeCompare(b.valueColumn ?? "") : "".localeCompare(b.valueColumn ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.ResultValue",
        {
          lngs: i18n.languages,
          defaultValue: "Result Value",
        }
      ),
      key: "resultValue",
      dataIndex: "resultValue",
      width: "200px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "code",
      //   onCodeFilterChange,
      //   codeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onCodeTreeItemCheck,
      //   codeAscending,
      //   codeDescending,
      //   setCodeAscending,
      //   setCodeDescending
      // ),
      sorter: (a, b) => a.resultValue ? a.resultValue.localeCompare(b.resultValue ?? "") : "".localeCompare(b.resultValue ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.ConditionValue",
        {
          lngs: i18n.languages,
          defaultValue: "Condition Value",
        }
      ),
      key: "conditionValue",
      dataIndex: "conditionValue",
      width: "200px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "code",
      //   onCodeFilterChange,
      //   codeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onCodeTreeItemCheck,
      //   codeAscending,
      //   codeDescending,
      //   setCodeAscending,
      //   setCodeDescending
      // ),
      sorter: (a, b) => a.conditionValue ? a.conditionValue.localeCompare(b.conditionValue ?? "") : "".localeCompare(b.conditionValue ?? ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.MappingMigrationStatus",
        {
          lngs: i18n.languages,
          defaultValue: "Mapping Migration Status",
        }
      ),
      key: "mappingMigrationStatus",
      dataIndex: "mappingMigrationStatus",
      width: "200px",
      render: (value) => value === true ? "MIGRATION" : "NO MIGRATION",
      filters: [
        {
          text: t(
            "Page.Operation.MigrationRules.Tabs.RuleResults.Label.Migration",
            {
              lngs: i18n.languages,
              defaultValue: "MIGRATION",
            }
          ),
          value: "MIGRATION",
        },
        {
          text: t(
            "Page.Operation.MigrationRules.Tabs.RuleResults.Label.NoMigration",
            {
              lngs: i18n.languages,
              defaultValue: "NO MIGRATION",
            }
          ),
          value: "NO MIGRATION",
        },
      ],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "code",
      //   onCodeFilterChange,
      //   codeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onCodeTreeItemCheck,
      //   codeAscending,
      //   codeDescending,
      //   setCodeAscending,
      //   setCodeDescending
      // ),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.MappingWeight",
        {
          lngs: i18n.languages,
          defaultValue: "Mapping Weight",
        }
      ),
      key: "mappingWeight",
      dataIndex: "mappingWeight",
      width: "135px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "code",
      //   onCodeFilterChange,
      //   codeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onCodeTreeItemCheck,
      //   codeAscending,
      //   codeDescending,
      //   setCodeAscending,
      //   setCodeDescending
      // ),
      sorter: (a, b) => a.mappingWeight - b.mappingWeight,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.ResultMigrationStatus",
        {
          lngs: i18n.languages,
          defaultValue: "Result Migration Status",
        }
      ),
      key: "resultMigrationStatus",
      width: "200px",
      dataIndex: "resultMigrationStatus",
      render: (value) => value ? "MIGRATION" : "NO MIGRATION",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "code",
      //   onCodeFilterChange,
      //   codeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onCodeTreeItemCheck,
      //   codeAscending,
      //   codeDescending,
      //   setCodeAscending,
      //   setCodeDescending
      // ),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleResults.DetailsTable.Headers.ResultWeight",
        {
          lngs: i18n.languages,
          defaultValue: "Result Weight",
        }
      ),
      key: "resultWeight",
      width: "130px",
      dataIndex: "resultWeight",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "code",
      //   onCodeFilterChange,
      //   codeFilterTreeData,
      //   groupCodeCheckedKeys,
      //   onCodeTreeItemCheck,
      //   codeAscending,
      //   codeDescending,
      //   setCodeAscending,
      //   setCodeDescending
      // ),
      sorter: (a, b) => a.resultWeight - b.resultWeight,
    },
  ];

  return (
    <>
      {!selectedEntity || !selectedGroupCode ? (
        <Empty
          style={{ display: "flex", width: "100%" }}
          description={t(
            "Page.Operation.MigrationRules.Tabs.RuleResults.Tutorial.GetStarted",
            {
              lngs: i18n.languages,
              defaultValue:
                "Please select a Group Code and an Entity to get started",
            }
          )}
        ></Empty>
      ) : (
        <Datatable
          scroll={scrollMain}
          tableColumns={columns}
          data={data}
          setData={setData}
          isTableLoading={isTableLoading}
          otherActions={[]}
          otherRowEnabled={false}
          editRowEnabled={false}
          copyRowEnabled={false}
          deleteRowEnabled={false}
          onEditFunc={undefined}
          onCopyFunc={undefined}
          onDeleteFunc={undefined}
          onDoubleClickRow={onDoubleClickRow}
          selectable={selectable}
          multiSelect={multiSelect}
          selectedRows={selectedRows}
          setSelectedRows={undefined}
          checkPermissions={true}
          showActionsColumn={false}
          sticky={false}
          paginationDisabled={true}
        ></Datatable>
      )}
      <Drawer
        width={"400px"}
        height={"95vh"}
        getContainer={false}
        title={t(
          "Page.Operation.MigrationRules.Tabs.RuleResults.Labels.RowDetails",
          {
            lngs: i18n.languages,
            defaultValue: "Row details",
          }
        )}
        style={{ position: "absolute", transform: "none" }}
        bodyStyle={{
          display: "flex",
          flexDirection: "column",
          transform: "none",
        }}
        placement={DRAWER_PLACEMENTS.BOTTOM}
        onClose={onClose}
        visible={openDrawer}
        closable={false}
        extra={
          <Button
            type="ghost"
            onClick={onClose}
            icon={<CloseOutlined />}
            size="small"
          />
        }
      >
        <Datatable
          scroll={scroll}
          tableColumns={columnsDetails}
          data={detailsData}
          setData={setDetailsData}
          isTableLoading={isTableLoading}
          otherActions={[]}
          otherRowEnabled={false}
          editRowEnabled={false}
          copyRowEnabled={false}
          deleteRowEnabled={false}
          onEditFunc={undefined}
          onCopyFunc={undefined}
          onDeleteFunc={undefined}
          onDoubleClickRow={onDoubleClickRow}
          selectable={selectable}
          multiSelect={multiSelect}
          selectedRows={selectedRows}
          setSelectedRows={undefined}
          checkPermissions={true}
          showActionsColumn={false}
          sticky={false}
        ></Datatable>
      </Drawer>
    </>
  );
};

export default RuleResults;
